// ...existing code...
import { Row, Button, Col, Card, Container } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { User } from "./features/user/userSlice";
import { useAppSelector } from "./app/hooks";
import { selectConfig } from "./features/config/configSlice";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";

const AnimatedCard = ({
  url,
  delay,
  svgIcon,
  category,
  title,
  description,
  className,
  imageSrc,
  ribbon,
}: {
  url?: string;
  delay: number;
  svgIcon?: React.ReactNode;
  category?: string;
  title: string;
  description: string;
  className?: string;
  imageSrc?: string;
  ribbon?: string;
}) => {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);
  const { t, i18n } = useTranslation();
  return (
    <div className={`fade-in-up${visible ? " visible" : ""}`}>
      <Card
        className="overflow-hidden rounded-4 border-0 shadow-lg text-white"
        style={{ backgroundColor: "transparent", minHeight: 300 }}
      >
        <div style={{ position: "relative" }}>
          {ribbon && <div className="corner-ribbon">{ribbon}</div>}
          <Card.Img src={imageSrc} alt={title} style={{ height: "200px", objectFit: "cover" }} />
          <div className="app-card">
            <div className="app-card-overlay">{svgIcon}</div>

            <div
              style={{
                fontSize: "0.75rem",
                letterSpacing: "1px",
                textTransform: "uppercase",
                opacity: 0.8,
              }}
            >
              {category}
            </div>
            <h2 style={{ fontSize: "1.5rem", margin: 0 }}>{title}</h2>
          </div>
        </div>
        <Card.Body className="glass-pane" style={{ color: "#fff", lineHeight: 1.6 }}>
          <Row className="justify-content-center align-items-bottom p-1">
            {description.split("\n").map((line, idx) => (
              <p key={idx} style={{ marginBottom: "0.5rem" }}>
                {line}
              </p>
            ))}
          </Row>
          {url && (
            <>
              <hr />
              <Row className="justify-content-center align-items-center p-1">
                <Col md={7} className="p-0 text-center">
                  {url && (
                    <Button className="sx-button" onClick={() => window.open(url, "_blank", "noopener,noreferrer")}>
                      {t("applications_page.go_to")} {title}
                    </Button>
                  )}
                </Col>
              </Row>
            </>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

interface ApplicationPageProps {
  lang?: string;
  user?: User | null;
}

export default function ApplicationsPage(props: ApplicationPageProps) {
  const { t, i18n } = useTranslation();
  const config = useAppSelector(selectConfig);

  return (
    <Container fluid className="mt-4 mb-5">
      <Row className="justify-content-center align-items-center">
        <Col sm={4}>
          <AnimatedCard
            category={t("applications_page.structural_design")}
            title="Simplex Beam"
            delay={200}
            url={config.BEAM_URL}
            imageSrc="https://strusoft.com/wp-content/uploads/2024/08/Intuative-user-interface.png"
            description={t("applications_page.beam_text")}
            svgIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="70"
                height="70"
                viewBox="0 0 135 135"
                className="simplex-app-icon"
              >
                <path d="M68.18,2.98C33.17,2.98,4.68,31.47,4.68,66.48s28.49,63.5,63.5,63.5,63.5-28.49,63.5-63.5S103.19,2.98,68.18,2.98ZM123.35,47.06h-41.12l8.98-16.07h-6.5V10.38c18.01,5.32,32.42,19.08,38.63,36.69ZM68.18,7.98c3.95,0,7.81.4,11.54,1.15v21.86h-6.5l8.98,16.07H13.01C21.03,24.32,42.73,7.98,68.18,7.98ZM68.18,124.98c-25.28,0-46.86-16.12-55.01-38.61h18.76l-8.71,15.09h18l-8.71-15.09h72.44c-3.05,1.13-5.24,4.05-5.24,7.5,0,4.42,3.58,8,8,8s8-3.58,8-8c0-3.45-2.18-6.37-5.24-7.5h12.73c-8.16,22.49-29.74,38.61-55.01,38.61ZM124.75,81.37H11.61c-1.25-4.76-1.93-9.74-1.93-14.89s.63-9.8,1.8-14.42h113.4c1.17,4.61,1.8,9.44,1.8,14.42s-.67,10.13-1.93,14.89Z" />
              </svg>
            }
          />
        </Col>
        <Col sm={4}>
          <AnimatedCard
            category={t("applications_page.structural_design")}
            className="glass-pane"
            delay={400}
            imageSrc="https://strusoft.com/wp-content/uploads/2025/04/UI-1980-1.png"
            title="Simplex Foundation"
            url={config.FOUNDATION_URL}
            description={t("applications_page.foundation_text")}
            svgIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="70"
                height="70"
                viewBox="0 0 135 135"
                className="simplex-app-icon"
              >
                <path d="M67.44,4C32.43,4,3.94,32.49,3.94,67.5s28.49,63.5,63.5,63.5,63.5-28.49,63.5-63.5S102.46,4,67.44,4ZM123.7,51.49h-34.48V13.22c16.64,6.7,29.51,20.83,34.48,38.27ZM84.22,11.46v40.53c0,2.48,2.02,4.5,4.5,4.5h36.17c.68,3.57,1.05,7.24,1.05,11.01,0,5.03-.64,9.92-1.84,14.58H10.78c-1.2-4.66-1.84-9.55-1.84-14.58,0-3.76.37-7.44,1.05-11.01h36.53c2.48,0,4.49-2.01,4.49-4.49V11.36c5.21-1.53,10.72-2.36,16.42-2.36s11.46.87,16.78,2.46ZM46.02,13.08v38.41H11.19c5-17.56,18.02-31.77,34.83-38.41ZM67.44,126c-25.39,0-47.04-16.26-55.12-38.92h17.35l-6.23,8.27,3.99,3.01,8.5-11.28h7.39l-6.23,8.27,3.99,3.01,8.5-11.28h8.88l-6.23,8.27,3.99,3.01,8.5-11.28h8.8l-6.23,8.27,3.99,3.01,8.5-11.28h8.05l-6.23,8.27,3.99,3.01,8.5-11.28h8.88l-6.23,8.27,3.99,3.01,8.5-11.28h13.34c-8.07,22.65-29.73,38.92-55.12,38.92Z" />
              </svg>
            }
          />
        </Col>
      </Row>
      <Row className="mt-4 justify-content-center align-items-center">
        <Col sm={4}>
          <AnimatedCard
            //category={props.user ? "BETA AVAILABLE" : "COMING SOON"}
            category={t("applications_page.coming_soon")}
            className="glass-pane"
            delay={600}
            imageSrc="https://strusoft.com/wp-content/uploads/2025/09/frame.png"
            title="Simplex Frame"
            ribbon={props.user ? t("applications_page.coming_soon") : t("applications_page.coming_soon")}
            // url={
            //   props.user
            //     ? "https://simplex-matrix-solutions-dev.private.strusoft.com/"
            //     : undefined
            // }
            description={t("applications_page.frame_text")}
            svgIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="70"
                height="70"
                viewBox="0 0 135 135"
                className="simplex-app-icon"
              >
                <path d="M97.1,11v-.06h-.11c-8.82-4.65-18.85-7.29-29.49-7.29C32.49,3.66,4,32.14,4,67.16s28.49,63.5,63.5,63.5,63.5-28.49,63.5-63.5c0-24.33-13.76-45.49-33.9-56.15ZM9,67.16c0-32.26,26.24-58.5,58.5-58.5,8.78,0,17.12,1.96,24.6,5.44v9.92h-3.53l5.57,8.78c-2.94.16-5.41,2.07-6.38,4.72h-40.86c-1.15-2.46-3.65-4.17-6.55-4.17-4,0-7.24,3.24-7.24,7.24,0,3.08,1.93,5.71,4.65,6.75v40.21c-2.72,1.04-4.65,3.67-4.65,6.75,0,3.83,2.97,6.95,6.73,7.21l-7.25,12.55c-14.31-10.67-23.59-27.73-23.59-46.91ZM47.13,91.81c-.07-.2-.16-.4-.25-.59l44.63-44.63c.17.08.35.15.53.22v40.72c-1.98.73-3.55,2.3-4.29,4.29h-40.63ZM87.76,42.52c.07.18.14.36.22.53l-44.66,44.66c-.18-.08-.37-.16-.56-.22v-40.08c2.21-.78,3.93-2.6,4.56-4.88h40.44ZM33.54,114.74h14.95l-7.64-13.23c2.89-.2,5.32-2.1,6.28-4.71h40.63c.95,2.58,3.32,4.46,6.16,4.71l-7.52,13.02h15.36c-9.64,6.99-21.47,11.13-34.26,11.13s-24.38-4.05-33.96-10.91ZM102.42,114.07l-7.25-12.55c3.7-.32,6.61-3.42,6.61-7.21,0-3.12-1.97-5.77-4.74-6.79v-40.72c2.76-1.02,4.74-3.67,4.74-6.79,0-3.82-2.97-6.95-6.73-7.21l5.58-8.79h-3.53v-7.29c17.28,10.18,28.9,28.97,28.9,50.43,0,19.18-9.28,36.23-23.58,46.91Z" />
              </svg>
            }
          />
        </Col>

        <Col sm={4}>
          <AnimatedCard
            category={t("applications_page.coming_soon")}
            className="glass-pane"
            delay={800}
            imageSrc="https://strusoft.com/wp-content/uploads/2025/09/column.png"
            title="Simplex Column"
            ribbon={t("applications_page.coming_soon")}
            description={t("applications_page.column_text")}
            svgIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="70"
                height="70"
                viewBox="0 0 135 135"
                className="simplex-app-icon"
              >
                <path d="M68.04,4C33.03,4,4.54,32.49,4.54,67.5s28.49,63.5,63.5,63.5,63.5-28.49,63.5-63.5S103.06,4,68.04,4ZM68.04,126c-32.26,0-58.5-26.24-58.5-58.5S34.44,10.38,65.54,9.06v17.13h-6.5l9,14.73,9-14.73h-6.5V9.06c31.1,1.32,56,27.02,56,58.44s-26.24,58.5-58.5,58.5Z" />
                <path d="M53.1,107.37h14.59l-8.65,14.98h18l-8.65-14.98h14.7V41.08h-30v66.29ZM58.1,46.08h20v56.29h-20v-56.29Z" />
              </svg>
            }
          />
        </Col>
      </Row>
    </Container>
  );
}
